/**
 * pre205: Codec-/Sender-Tuning für mobile Gäste.
 *
 * Der 1:1-Gast (Magic-Link) nutzt kein SFrame/E2EE (nur Gruppen-/Caller-Modus), braucht
 * also kein VP9. Auf Mobilgeräten hat VP9 meist KEINE Hardware-Encodierung → Software-
 * Encode → starker Lag / niedrige Framerate. H264 ist auf praktisch allen Handys in
 * Hardware verfügbar. Daher bevorzugen wir für Gast-Verbindungen H264. Die Stele behält
 * ihr VP9 (E2EE/Keyframe-Handling) — dieser Helfer wird nur auf Gast-Peers angewendet.
 */

export function preferVideoCodec(pc: RTCPeerConnection, want: 'H264' | 'VP8' | 'VP9') {
  try {
    if (!(RTCRtpTransceiver.prototype as any).setCodecPreferences) return;
    const caps = (RTCRtpSender as any).getCapabilities?.('video');
    if (!caps?.codecs) return;
    const target = `video/${want.toLowerCase()}`;
    const preferred = caps.codecs.filter((c: any) => c.mimeType.toLowerCase() === target);
    if (!preferred.length) return; // Codec nicht verfügbar → nichts ändern
    const rest = caps.codecs.filter((c: any) => c.mimeType.toLowerCase() !== target);
    const ordered = [...preferred, ...rest];
    for (const t of pc.getTransceivers()) {
      // Auf Audio-Transceivern wirft setCodecPreferences (Codec-Kind-Mismatch) → ignorieren.
      try { (t as any).setCodecPreferences(ordered); } catch { /* */ }
    }
  } catch { /* */ }
}

/**
 * Video-Sender zähmen: Bitrate/Framerate begrenzen und bei CPU-/Bandbreiten-Engpass lieber
 * die Auflösung senken als die Framerate (flüssiges Bild wichtiger als Schärfe im Live-Stream;
 * ein Scan greift ohnehin das Vollbild separat).
 */
export async function tuneVideoSender(pc: RTCPeerConnection, maxBitrate = 1_400_000, maxFramerate = 30) {
  try {
    const sender = pc.getSenders().find((s) => s.track && s.track.kind === 'video');
    if (!sender) return;
    if (sender.track) { try { (sender.track as any).contentHint = 'motion'; } catch { /* */ } }
    const p = sender.getParameters();
    if (!p.encodings || !p.encodings.length) (p as any).encodings = [{}];
    p.encodings[0].maxBitrate = maxBitrate;
    (p.encodings[0] as any).maxFramerate = maxFramerate;
    (p as any).degradationPreference = 'maintain-framerate';
    await sender.setParameters(p);
  } catch { /* */ }
}

/**
 * pre208: H264 in der Video-m-Zeile nach vorne ziehen (robuster als setCodecPreferences,
 * das Safari teils ignoriert). Nur für Nicht-E2EE-/Gast-Verbindungen anwenden.
 */
export function preferH264InSdp(sdp: string): string {
  try {
    const lines = sdp.split(/\r\n|\n/);
    const mIdx = lines.findIndex((l) => l.startsWith('m=video'));
    if (mIdx < 0) return sdp;
    const h264 = new Set<string>();
    for (const l of lines) {
      const m = l.match(/^a=rtpmap:(\d+)\s+H264\//i);
      if (m) h264.add(m[1]);
    }
    if (!h264.size) return sdp;
    const parts = lines[mIdx].split(' ');
    const head = parts.slice(0, 3);
    const pts = parts.slice(3);
    lines[mIdx] = [...head, ...pts.filter((p) => h264.has(p)), ...pts.filter((p) => !h264.has(p))].join(' ');
    return lines.join('\r\n');
  } catch { return sdp; }
}
